/**
 * Login Page
 */

import React from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { Controller } from "react-hook-form";
import { z } from "zod";
import { useNavigate, useLocation, Link as RouterLink, createPath, type Path, useSearchParams } from "react-router-dom";
import { useLogin } from "@/hooks/useAuth";
import { useAuthStore } from "@/store/authStore";
import { getApiErrorMessage } from "@/utils/formatters";
import { getTenantSlugFromHostname } from "@/utils/tenant";
import { AFTER_SIGN_IN_STATE, isSafeReturnPath } from "@/utils/navigation";
import { usePublicBranding } from "@/hooks/useTenants";
import { TenantBranding } from "@/components/common/TenantBranding";
import { useAppForm } from "@/hooks/useAppForm";
import { loginSchema as baseLoginSchema } from "@/utils/schemas";
import { wasSignedOutIdle } from "@/utils/idleSession";
import { AUTH_CONFIG } from "@/constants";
import { ServerUnreachable } from "@/components/auth/ServerUnreachable";
import { homePathForRole } from "@/utils/homePath";

// Shared login schema plus the optional multi-school tenant slug.
const loginSchema = baseLoginSchema.extend({
  tenant_slug: z.string().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Where to send the user after sign-in, from the location ProtectedRoute
 * stashed in router state. Keeps the query string (and hash) so deep links
 * like /parent/applications/new?student=<id> survive the login round-trip.
 */
export function redirectPathFromState(state: unknown): string | undefined {
  const from = (state as { from?: Partial<Path> } | null)?.from;
  if (!from?.pathname) return undefined;
  const path = createPath(from);
  return isSafeReturnPath(path) ? path : undefined;
}

/**
 * Post-login destination carried in `?next=` by navigateToLogin (the API
 * client's session-expiry redirect, which has no router state to stash).
 * Only same-origin absolute paths are honoured -- never an off-site URL.
 */
export function redirectPathFromNextParam(next: string | null): string | undefined {
  // `next` arrives from useSearchParams, which has already decoded it once.
  // Decoding again turned a path with a literal "%25" into something else
  // (change review 2026-09-15, portion 3); the safety check runs on the
  // value the router hands us.
  return isSafeReturnPath(next) ? next : undefined;
}

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The error shows in the form's Alert; no toast on top of it.
  const loginMutation = useLogin({ toastErrors: false });
  const { user: _user } = useAuthStore();
  const isHydrated = useAuthStore((s) => s.isHydrated);
  const serverUnreachable = useAuthStore((s) => s.serverUnreachable);
  const subdomainSlug = getTenantSlugFromHostname();
  const [showSchoolCode, setShowSchoolCode] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  // Read once: the idle sign-out may have happened while nobody was here.
  const [signedOutIdle] = React.useState(() => wasSignedOutIdle());
  const idleHours = AUTH_CONFIG.IDLE_TIMEOUT_MS / (60 * 60 * 1000);

  const { data: branding } = usePublicBranding(subdomainSlug);

  const [loginSearchParams] = useSearchParams();
  const from =
    redirectPathFromState(location.state) ??
    redirectPathFromNextParam(loginSearchParams.get("next"));

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useAppForm<LoginFormData>({
    schema: loginSchema,
    defaultValues: {
      email: "",
      password: "",
      remember_me: true,
      tenant_slug: subdomainSlug ?? "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    loginMutation.mutate(data, {
      onSuccess: (response) => {
        // Force password change if required
        if (response.data.user.must_change_password) {
          navigate("/change-password", { replace: true });
          return;
        }

        // Determine redirect based on user role
        const userRole = response.data.user.role;
        let redirectPath = from;
        // `from` may carry ?query#hash (deep links); compare the bare path.
        const fromPathname = redirectPath?.replace(/[?#].*$/, "");

        // If no specific path or trying to access wrong dashboard, redirect based on role
        const homePath = homePathForRole(userRole);
        if (!redirectPath || fromPathname === "/dashboard" || fromPathname === "/parent/dashboard" || fromPathname === "/teacher/dashboard" || fromPathname === "/platform") {
          redirectPath = homePath;
        }

        // Super users should go to platform admin
        if (userRole === "super_user" && !redirectPath.startsWith("/platform")) {
          redirectPath = "/platform";
        }

        // Only super_users may land on /platform. A non-super_user with a
        // saved /platform deep link (e.g. a bookmarked platform URL) would
        // otherwise be sent straight to Unauthorized — send them to their
        // own dashboard instead.
        if (userRole !== "super_user" && redirectPath.startsWith("/platform")) {
          redirectPath = homePath;
        }

        // Every other role returns to the page they were on. Whether they may
        // open it is the route guard's call (ProtectedRoute roles /
        // permission -- the same model as the sidebar and buttons), not a
        // per-role list here: AFTER_SIGN_IN_STATE makes a refused page send
        // them home instead of to Unauthorized.
        navigate(redirectPath, { replace: true, state: AFTER_SIGN_IN_STATE });
      },
    });
  };

  const supportEmail = branding?.support_email || "support@ilmiq.com";

  // A login that may still be live: no form that would pass for a sign-out.
  if (serverUnreachable) return <ServerUnreachable />;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: "100%",
          boxShadow: 3,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Logo & Title */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            {/* Branding Logo */}
            <Box sx={{ mb: 2 }}>
              <TenantBranding
                tenantName={branding?.name}
                size="large"
                showTenantName={false}
                logoUrl={branding?.logo_url}
                primaryColor={branding?.primary_color}
                secondaryColor={branding?.secondary_color}
              />
            </Box>

            <Typography variant="h5" gutterBottom sx={{
              fontWeight: 600
            }}>
              {branding?.welcome_title || "Welcome Back"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center"
              }}>
              {branding?.welcome_subtitle || "Sign in to your account"}
            </Typography>
          </Box>

          {signedOutIdle && !loginMutation.isError && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You were signed out after {idleHours} hours without activity.
              {from ? " Sign in again to pick up where you left off." : ""}
            </Alert>
          )}

          {/* Error Alert */}
          {loginMutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {getApiErrorMessage(loginMutation.error, "Login failed. Please try again.")}
            </Alert>
          )}

          {/* Login Form */}
          {/* noValidate: let the zod schema own validation so a malformed
              email shows the app's message, not the browser's native tooltip. */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email Address"
                  type="email"
                  fullWidth
                  margin="normal"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  autoComplete="email"
                  autoFocus
                />
              )}
            />

            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  margin="normal"
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  autoComplete="current-password"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                  }}
                />
              )}
            />

            {!subdomainSlug && showSchoolCode && (
              <Controller
                name="tenant_slug"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="School Code (Optional)"
                    fullWidth
                    margin="normal"
                    placeholder="e.g., dua"
                    helperText="Only needed if your email is used at multiple schools"
                  />
                )}
              />
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 3,
              }}
            >
              <Controller
                name="remember_me"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label="Remember me"
                  />
                )}
              />

              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                underline="hover"
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              // Not before the boot refresh settles: its result must not
              // land on top of a login typed meanwhile.
              disabled={loginMutation.isPending || !isHydrated}
              sx={{
                mb: 2,
                ...(subdomainSlug && branding?.primary_color
                  ? {
                      bgcolor: branding.primary_color,
                      "&:hover": { bgcolor: branding.primary_color, filter: "brightness(0.9)" },
                    }
                  : {}),
              }}
            >
              {loginMutation.isPending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Footer */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Need help?{" "}
              <Link href={`mailto:${supportEmail}`} underline="hover">
                Contact Support
              </Link>
            </Typography>
            {!subdomainSlug && !showSchoolCode && (
              <Link
                component="button"
                variant="body2"
                onClick={() => setShowSchoolCode(true)}
                sx={{ mt: 1, display: "block" }}
              >
                Have accounts at multiple schools?
              </Link>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
